import React, {useState} from "react";
import {EventResult} from "event-series-shared/src/types/EventResult";
import {updateEvent, deleteEvent} from "../Api";

const TrashIcon = () => (
    <svg width="11" height="13" viewBox="0 0 11 13" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="0.5" y1="3" x2="10.5" y2="3"/>
        <path d="M2 3v8.5a.5.5 0 0 0 .5.5h6a.5.5 0 0 0 .5-.5V3"/>
        <path d="M3.5 3V1.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V3"/>
        <line x1="4.5" y1="5.5" x2="4.5" y2="9.5"/>
        <line x1="6.5" y1="5.5" x2="6.5" y2="9.5"/>
    </svg>
);

interface Props {
    event: EventResult;
    onSaved: () => void;
    onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');

const toDateStr = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const toTimeStr = (d: Date) =>
    `${pad(d.getHours())}:${pad(d.getMinutes())}`;

function EditEventModal({event, onSaved, onClose}: Props) {

    const startDate = new Date(String(event.start));
    const endDate   = new Date(String(event.end));

    const [startDateStr, setStartDateStr] = useState(toDateStr(startDate));
    const [startTimeStr, setStartTimeStr] = useState(toTimeStr(startDate));
    const [endDateStr,   setEndDateStr]   = useState(toDateStr(endDate));
    const [endTimeStr,   setEndTimeStr]   = useState(toTimeStr(endDate));
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!startDateStr || !startTimeStr || !endDateStr || !endTimeStr) {
            setFormError('Please fill in both times.');
            return;
        }
        if (new Date(`${endDateStr}T${endTimeStr}`) <= new Date(`${startDateStr}T${startTimeStr}`)) {
            setFormError('End time must be after start time.');
            return;
        }
        setSaving(true);
        try {
            await updateEvent(event.eventId, `${startDateStr}T${startTimeStr}`, `${endDateStr}T${endTimeStr}`);
            onSaved();
        } catch (e) {
            setFormError('Failed to save.');
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await deleteEvent(event.eventId);
            onSaved();
        } catch (e) {
            setFormError('Failed to delete.');
            setSaving(false);
        }
    };

    const endTimeMin = endDateStr === startDateStr ? startTimeStr : '';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-title">Edit Event</div>

                <div className="modal-field">
                    <label>Start</label>
                    <div className="modal-datetime">
                        <input type="date" value={startDateStr} onChange={e => setStartDateStr(e.target.value)}/>
                        <input type="time" value={startTimeStr} onChange={e => setStartTimeStr(e.target.value)}/>
                    </div>
                </div>
                <div className="modal-field">
                    <label>End</label>
                    <div className="modal-datetime">
                        <input type="date" value={endDateStr} min={startDateStr} onChange={e => setEndDateStr(e.target.value)}/>
                        <input type="time" value={endTimeStr} min={endTimeMin} onChange={e => setEndTimeStr(e.target.value)}/>
                    </div>
                </div>
                {formError && <div className="modal-error">{formError}</div>}

                <div className="modal-buttons">
                    <button className="modal-btn danger" onClick={handleDelete} disabled={saving}><TrashIcon /></button>
                    <button className="modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="modal-btn primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EditEventModal;
